import React, { useState } from 'react';
import OverlayTextArea from '../components/OverlayTextArea';
import SerpPreview from '../components/SerpPreview';
import SeoText from '../components/SeoText';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import CharacterCount from '../components/CharacterCount';
import InfoIcon from '../components/InfoIcon';

function TitleMeta() {
  const [title, setTitle] = useState('');
  const [metaDesc, setMetaDesc] = useState('');
  const [showSecondVersion, setShowSecondVersion] = useState(false);
  const [title2, setTitle2] = useState('');
  const [metaDesc2, setMetaDesc2] = useState('');

  // Copy & Clear handlers for first version
  const handleTitleClear = () => setTitle('');
  const handleTitleCopy = () => navigator.clipboard?.writeText(title);
  const handleMetaClear = () => setMetaDesc('');
  const handleMetaCopy = () => navigator.clipboard?.writeText(metaDesc);

  // Copy & Clear for second version
  const handleTitle2Clear = () => setTitle2('');
  const handleTitle2Copy = () => navigator.clipboard?.writeText(title2);
  const handleMeta2Clear = () => setMetaDesc2('');
  const handleMeta2Copy = () => navigator.clipboard?.writeText(metaDesc2);

  return (
    <>
      {/* Tools Section */}
      <div className="max-w-6xl mx-auto px-6 pb-12">
        <div className="flex gap-6">
          {/* Left column: text fields */}
          <Card className="flex-1">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Title & Meta Editor</CardTitle>
              <Button 
                onClick={() => setShowSecondVersion(true)} 
                variant="secondary"
                size="sm"
                disabled={showSecondVersion}
              >
                Add Version
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Version 1 */}
              <div className="space-y-4">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium text-text-primary">Title</h4>
                      <InfoIcon 
                        text="Click here for tips on how to make your title better" 
                        href="#seo-title-tips" 
                      />
                    </div>
                    <CharacterCount count={title.length} limit={60} type="title" text={title} />
                  </div>
                  <OverlayTextArea
                    value={title}
                    onChange={setTitle}
                    rows={2}
                    limit={60}
                    limitType="char"
                    className="mb-2 min-h-[3.5rem] max-h-[3.5rem]"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" size="sm" onClick={handleTitleClear}>Clear</Button>
                    <Button variant="secondary" size="sm" onClick={handleTitleCopy}>Copy</Button>
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium text-text-primary">Meta Description</h4>
                      <InfoIcon 
                        text="Click here for tips on how to make your meta description better" 
                        href="#meta-description-tips" 
                      />
                    </div>
                    <CharacterCount count={metaDesc.length} limit={160} type="meta" text={metaDesc} />
                  </div>
                  <OverlayTextArea
                    value={metaDesc}
                    onChange={setMetaDesc}
                    rows={4}
                    limit={160}
                    limitType="char"
                    className="mb-2 min-h-[7rem] max-h-[7rem]"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" size="sm" onClick={handleMetaClear}>Clear</Button>
                    <Button variant="secondary" size="sm" onClick={handleMetaCopy}>Copy</Button>
                  </div>
                </div>
              </div>

              {/* Version 2 */}
              {showSecondVersion && (
                <div className="space-y-4 border-t border-border pt-6">
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="text-sm font-medium text-text-primary">Title (Version 2)</h4>
                      <CharacterCount count={title2.length} limit={60} type="title" text={title2} />
                    </div>
                    <OverlayTextArea
                      value={title2}
                      onChange={setTitle2}
                      rows={2}
                      limit={60}
                      limitType="char"
                      className="mb-2 min-h-[3.5rem] max-h-[3.5rem]"
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={handleTitle2Clear}>Clear</Button>
                      <Button variant="secondary" size="sm" onClick={handleTitle2Copy}>Copy</Button>
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="text-sm font-medium text-text-primary">Meta Description (Version 2)</h4>
                      <CharacterCount count={metaDesc2.length} limit={160} type="meta" text={metaDesc2} />
                    </div>
                    <OverlayTextArea
                      value={metaDesc2}
                      onChange={setMetaDesc2}
                      rows={4}
                      limit={160}
                      limitType="char"
                      className="mb-2 min-h-[7rem] max-h-[7rem]"
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={handleMeta2Clear}>Clear</Button>
                      <Button variant="secondary" size="sm" onClick={handleMeta2Copy}>Copy</Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right column: SERP preview(s) */}
          <Card className="flex-1">
            <CardHeader>
              <CardTitle>SERP Preview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <SerpPreview title={title} metaDesc={metaDesc} />

              {showSecondVersion && (
                <>
                  <div className="border-t border-border pt-6">
                    <h4 className="mb-4 text-sm font-medium text-text-primary">Version 2</h4>
                    <SerpPreview title={title2} metaDesc={metaDesc2} />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Documentation Section - White Background */}
      <div className="bg-white">
        <SeoText />
      </div>
    </>
  );
}

export default TitleMeta;